/* eslint-disable no-fallthrough */
import { capitalize, getPluralFormatter } from 'common/helpers'
import type { Nullable } from 'common/types'

interface IDateTimeProvider {
  getNow: () => Date
}

interface IRelativeDateTimeFormatter {
  relativeDate(date: Nullable<Date>): string
}

const connectMinutes = getPluralFormatter('минуту')

const defaultDateTimeProvider: IDateTimeProvider = { getNow: () => new Date() }

export class DateTimeFormatter implements IRelativeDateTimeFormatter {
  public constructor(private readonly provider: IDateTimeProvider = defaultDateTimeProvider) {}

  private timeFormatter = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' })
  private dateTimeFormatter = new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  })

  public relativeDate = (dateRepresentation: Nullable<DateRepresentation | undefined>): string => {
    const date = parseDate(dateRepresentation)
    if (!date) {
      return ''
    }

    switch (true) {
      case this.lessThanMinute(date):
        return 'Только что'
      case this.lessThanHour(date): {
        const minutes = Math.floor(this.dateDiffFromNow(date, 'm'))
        return `${minutes} ${connectMinutes(minutes)} назад`
      }
      case this.isToday(date): {
        return `Сегодня в ${this.timeFormatter.format(date)}`
      }
      case this.isYesterday(date): {
        return `Вчера в ${this.timeFormatter.format(date)}`
      }
      default: {
        return this.formatFullDate(date)
      }
    }
  }

  private formatFullDate(date: Date): string {
    return this.dateTimeFormatter
      .formatToParts(date)
      .map(part => (part.type === 'month' ? capitalize(part.value) : part.value))
      .join('')
      .replace(' г.', '')
  }

  private lessThanHour(date: Date): boolean {
    return this.dateDiffFromNow(date, 'm') < 60
  }

  private lessThanMinute(date: Date): boolean {
    return this.dateDiffFromNow(date, 's') < 60
  }

  private isToday(date: Date): boolean {
    const currentDate = this.provider.getNow()

    return (
      currentDate.getUTCFullYear() === date.getUTCFullYear() &&
      currentDate.getUTCMonth() === date.getUTCMonth() &&
      currentDate.getUTCDate() === date.getUTCDate()
    )
  }

  private isYesterday(date: Date): boolean {
    const currentDate = this.provider.getNow()
    return (
      currentDate.getUTCFullYear() === date.getUTCFullYear() &&
      currentDate.getUTCMonth() === date.getUTCMonth() &&
      currentDate.getUTCDate() - date.getUTCDate() <= 1
    )
  }

  private dateDiffFromNow(date: Date, unit: 'ms' | 's' | 'm' | 'h' = 'ms'): number {
    const now = this.provider.getNow()
    let diff = now.getTime() - date.getTime()

    switch (unit) {
      case 'h':
        diff /= 60
      case 'm':
        diff /= 60
      case 's':
        diff /= 1000
      case 'ms':
        return diff
    }
  }
}

export type DateRepresentation = string | number | Date

export function parseDate(date?: Nullable<DateRepresentation>): Nullable<Date> {
  if (!date || date === 'Invalid Date') {
    return null
  }
  return new Date(date)
}

export const relativeDateFormatter = new DateTimeFormatter()

export function compareDates(
  one: DateRepresentation | undefined,
  other: DateRepresentation | undefined,
) {
  const oneTime = parseDate(one)?.getTime() ?? 0
  const otherTime = parseDate(other)?.getTime() ?? 0
  return oneTime - otherTime
}
